/**
 * Normalizes supported logo HTML shapes into a fixed-footprint logo slot.
 *
 * The helper is intentionally DOM-only so it can run against jsdom in Node and
 * native DOMParser output in the browser.
 */

import {
  LOGO_ANCHOR_SELECTORS,
  LOGO_INNER_SELECTOR,
  isStructuralLogoCandidate
} from './logo-dom.js';

const LOGO_BARE_IMAGE_SELECTORS = ['img.cs-logo', 'img.logo'];

const LOGO_SLOT_CLASS = 'logo-slot';
const LOGO_BREAKPOINTS = ['mobile', 'tablet', 'desktop'];
const LOGO_ENGAGED_ATTR = 'data-logo-engaged';
const LOGO_SIZED_ATTR = 'data-logo-sized';
const LOGO_RUNTIME_MARKER = 'pb-logo-runtime-v2';

export const LOGO_RUNTIME_CSS = `/* ${LOGO_RUNTIME_MARKER} */
.logo,.cs-logo,.navbar-brand,.brand,.footer-logo{--pb-logo-hover-scale:1;--pb-logo-hover-y:0rem;--pb-logo-hover-rotate:0deg;--pb-logo-hover-filter:none;--pb-logo-hover-opacity:1}
.logo-slot{position:relative;display:inline-block;--pb-logo-tx-active:var(--pb-logo-tx,0);--pb-logo-ty-active:var(--pb-logo-ty,0);--pb-logo-tx-pct-active:var(--pb-logo-tx-pct,0%);--pb-logo-ty-pct-active:var(--pb-logo-ty-pct,0%);--pb-logo-scale-active:var(--pb-logo-scale,1);--pb-logo-slot-width-active:var(--pb-logo-slot-width,100%);--pb-logo-slot-height-active:var(--pb-logo-slot-height,100%);width:var(--pb-logo-slot-width-active,100%);max-width:100%;height:var(--pb-logo-slot-height-active,100%);aspect-ratio:var(--pb-logo-aspect-ratio,auto);flex-shrink:0;transition:width .2s ease,height .2s ease}
.logo-slot>img,.logo-slot>svg,.logo-slot>picture{display:block;width:100%!important;height:100%!important;object-fit:contain;object-position:center;transform-origin:center center;transform:translate(var(--pb-logo-tx-pct-active,0%),var(--pb-logo-ty-pct-active,0%)) translateY(var(--pb-logo-hover-y,0rem)) rotate(var(--pb-logo-hover-rotate,0deg)) scale(var(--pb-logo-scale-active,1)) scale(var(--pb-logo-hover-scale,1));filter:var(--pb-logo-hover-filter,none);opacity:var(--pb-logo-hover-opacity,1);transition:transform .25s ease,filter .25s ease,opacity .25s ease}
.logo-slot>img:not(:first-child),.logo-slot>svg:not(:first-child),.logo-slot>picture:not(:first-child){position:absolute!important;inset:0}
.logo-slot>picture>img{width:100%!important;height:100%!important;object-fit:contain;object-position:center}
.logo-slot[data-logo-sized="true"]>img,.logo-slot[data-logo-sized="true"]>svg,.logo-slot[data-logo-sized="true"]>picture,.logo-slot[data-logo-sized="true"]>picture>img{max-height:none!important}
.logo-slot[data-logo-replacing]>img,.logo-slot[data-logo-replacing]>svg,.logo-slot[data-logo-replacing]>picture{animation:pb-logo-replace .2s ease-out}
.logo:hover,.cs-logo:hover,.navbar-brand:hover,.brand:hover,.footer-logo:hover{cursor:pointer}
.logo[data-logo-hover="scale"]:hover,.cs-logo[data-logo-hover="scale"]:hover,.navbar-brand[data-logo-hover="scale"]:hover,.brand[data-logo-hover="scale"]:hover,.footer-logo[data-logo-hover="scale"]:hover{--pb-logo-hover-scale:1.05}
.logo[data-logo-hover="lift"]:hover,.cs-logo[data-logo-hover="lift"]:hover,.navbar-brand[data-logo-hover="lift"]:hover,.brand[data-logo-hover="lift"]:hover,.footer-logo[data-logo-hover="lift"]:hover{--pb-logo-hover-y:-.125rem;--pb-logo-hover-filter:drop-shadow(0 .5rem .75rem rgba(0,0,0,.18))}
.logo[data-logo-hover="rotate"]:hover,.cs-logo[data-logo-hover="rotate"]:hover,.navbar-brand[data-logo-hover="rotate"]:hover,.brand[data-logo-hover="rotate"]:hover,.footer-logo[data-logo-hover="rotate"]:hover{--pb-logo-hover-rotate:-3deg}
.logo[data-logo-hover="shimmer"]:hover .logo-slot>img,.logo[data-logo-hover="shimmer"]:hover .logo-slot>svg,.logo[data-logo-hover="shimmer"]:hover .logo-slot>picture,.cs-logo[data-logo-hover="shimmer"]:hover .logo-slot>img,.cs-logo[data-logo-hover="shimmer"]:hover .logo-slot>svg,.cs-logo[data-logo-hover="shimmer"]:hover .logo-slot>picture,.navbar-brand[data-logo-hover="shimmer"]:hover .logo-slot>img,.navbar-brand[data-logo-hover="shimmer"]:hover .logo-slot>svg,.navbar-brand[data-logo-hover="shimmer"]:hover .logo-slot>picture,.brand[data-logo-hover="shimmer"]:hover .logo-slot>img,.brand[data-logo-hover="shimmer"]:hover .logo-slot>svg,.brand[data-logo-hover="shimmer"]:hover .logo-slot>picture,.footer-logo[data-logo-hover="shimmer"]:hover .logo-slot>img,.footer-logo[data-logo-hover="shimmer"]:hover .logo-slot>svg,.footer-logo[data-logo-hover="shimmer"]:hover .logo-slot>picture{animation:pb-logo-shimmer .8s ease-in-out}
.logo[data-logo-hover="color-shift"]:hover,.cs-logo[data-logo-hover="color-shift"]:hover,.navbar-brand[data-logo-hover="color-shift"]:hover,.brand[data-logo-hover="color-shift"]:hover,.footer-logo[data-logo-hover="color-shift"]:hover{--pb-logo-hover-filter:hue-rotate(24deg)}
@media (max-width:47.999rem){.logo-slot{--pb-logo-tx-active:var(--pb-logo-tx-mobile,var(--pb-logo-tx,0));--pb-logo-ty-active:var(--pb-logo-ty-mobile,var(--pb-logo-ty,0));--pb-logo-tx-pct-active:var(--pb-logo-tx-pct-mobile,var(--pb-logo-tx-pct,0%));--pb-logo-ty-pct-active:var(--pb-logo-ty-pct-mobile,var(--pb-logo-ty-pct,0%));--pb-logo-scale-active:var(--pb-logo-scale-mobile,var(--pb-logo-scale,1));--pb-logo-slot-width-active:var(--pb-logo-slot-width-mobile,var(--pb-logo-slot-width,100%));--pb-logo-slot-height-active:var(--pb-logo-slot-height-mobile,var(--pb-logo-slot-height,100%))}}
@media (min-width:48rem) and (max-width:63.999rem){.logo-slot{--pb-logo-tx-active:var(--pb-logo-tx-tablet,var(--pb-logo-tx,0));--pb-logo-ty-active:var(--pb-logo-ty-tablet,var(--pb-logo-ty,0));--pb-logo-tx-pct-active:var(--pb-logo-tx-pct-tablet,var(--pb-logo-tx-pct,0%));--pb-logo-ty-pct-active:var(--pb-logo-ty-pct-tablet,var(--pb-logo-ty-pct,0%));--pb-logo-scale-active:var(--pb-logo-scale-tablet,var(--pb-logo-scale,1));--pb-logo-slot-width-active:var(--pb-logo-slot-width-tablet,var(--pb-logo-slot-width,100%));--pb-logo-slot-height-active:var(--pb-logo-slot-height-tablet,var(--pb-logo-slot-height,100%))}}
@media (min-width:64rem){.logo-slot{--pb-logo-tx-active:var(--pb-logo-tx-desktop,var(--pb-logo-tx,0));--pb-logo-ty-active:var(--pb-logo-ty-desktop,var(--pb-logo-ty,0));--pb-logo-tx-pct-active:var(--pb-logo-tx-pct-desktop,var(--pb-logo-tx-pct,0%));--pb-logo-ty-pct-active:var(--pb-logo-ty-pct-desktop,var(--pb-logo-ty-pct,0%));--pb-logo-scale-active:var(--pb-logo-scale-desktop,var(--pb-logo-scale,1));--pb-logo-slot-width-active:var(--pb-logo-slot-width-desktop,var(--pb-logo-slot-width,100%));--pb-logo-slot-height-active:var(--pb-logo-slot-height-desktop,var(--pb-logo-slot-height,100%))}}
@keyframes pb-logo-shimmer{0%,100%{opacity:1}50%{opacity:.68}}
@keyframes pb-logo-replace{from{opacity:0}to{opacity:var(--pb-logo-hover-opacity,1)}}
@media (prefers-reduced-motion:reduce){.logo,.logo:hover,.logo[data-logo-hover]:hover,.cs-logo,.cs-logo:hover,.cs-logo[data-logo-hover]:hover,.navbar-brand,.navbar-brand:hover,.navbar-brand[data-logo-hover]:hover,.brand,.brand:hover,.brand[data-logo-hover]:hover,.footer-logo,.footer-logo:hover,.footer-logo[data-logo-hover]:hover{--pb-logo-hover-scale:1;--pb-logo-hover-y:0rem;--pb-logo-hover-rotate:0deg;--pb-logo-hover-filter:none}.logo-slot{transition:none!important}.logo-slot>img,.logo-slot>svg,.logo-slot>picture{animation:none!important;transition:none!important}}`;

export function withLogoRuntimeCSS(css = '') {
  const rawCSS = typeof css === 'string' ? css : '';
  if (rawCSS.includes(LOGO_RUNTIME_MARKER)) return rawCSS;
  const baseCSS = rawCSS.trimEnd();
  return [baseCSS, LOGO_RUNTIME_CSS].filter(Boolean).join('\n\n');
}

function isElement(node) {
  return !!node && node.nodeType === 1;
}

function matchesAny(el, selectors) {
  if (!isElement(el) || typeof el.matches !== 'function') return false;
  return selectors.some((selector) => el.matches(selector));
}

function closestLogoWrapper(el) {
  if (!isElement(el)) return null;
  return el.closest(LOGO_ANCHOR_SELECTORS.join(','));
}

function firstAncestorMatching(el, selector, stopAt) {
  let current = el.parentElement;
  while (current && current !== stopAt) {
    if (current.matches(selector)) return current;
    current = current.parentElement;
  }
  return null;
}

function hasUsableLogoInner(wrapper) {
  return !!wrapper?.querySelector?.(LOGO_INNER_SELECTOR);
}

function findLogoInnerNodes(wrapper) {
  const nodes = [];
  for (const el of wrapper.querySelectorAll(LOGO_INNER_SELECTOR)) {
    if (el.closest(`.${LOGO_SLOT_CLASS}`)) continue;
    if (firstAncestorMatching(el, LOGO_INNER_SELECTOR, wrapper)) continue;
    nodes.push(el);
  }
  return nodes;
}

function readNumericAttr(el, attr) {
  const raw = el?.getAttribute?.(attr);
  if (!raw) return null;
  const match = String(raw)
    .trim()
    .match(/^([0-9]*\.?[0-9]+)/);
  if (!match) return null;
  const value = Number.parseFloat(match[1]);
  return Number.isFinite(value) && value > 0 ? value : null;
}

// Optional intrinsic-aspect resolver, supplied by the parser (which can read
// the asset off the crawl) and absent in the browser. See inferLogoAspectRatio.
let intrinsicAspectResolver = null;

function ratioOf(text) {
  const m = /^\s*([\d.]+)\s*\/\s*([\d.]+)\s*$/.exec(String(text || ''));
  if (!m) return null;
  const w = Number(m[1]);
  const h = Number(m[2]);
  return w > 0 && h > 0 ? w / h : null;
}

function inferLogoAspectRatio(innerNodes) {
  const first =
    innerNodes.find((node) => node.tagName === 'IMG') ||
    innerNodes.find((node) => node.tagName === 'PICTURE')?.querySelector('img') ||
    innerNodes[0];
  const width = readNumericAttr(first, 'width');
  const height = readNumericAttr(first, 'height');
  if (width && height) {
    const fromAttrs = `${width} / ${height}`;
    // The width/height ATTRIBUTES are the author's hint, not the asset's truth,
    // and they routinely disagree: a82website.pages.dev ships
    // `<img width="240" height="32">` for a logo whose SVG viewBox is
    // `0 0 220.83 57`. The live site lets height stay auto, so it paints at the
    // real 3.87 ratio; the slot pinned 7.5 and squashed the logo to half
    // height. Trust the asset when we can read it and the two disagree.
    const src = first?.getAttribute?.('src');
    const intrinsic = src && intrinsicAspectResolver ? intrinsicAspectResolver(src) : null;
    const a = ratioOf(fromAttrs);
    const b = ratioOf(intrinsic);
    if (a && b && Math.abs(a - b) / b > 0.02) return intrinsic;
    return fromAttrs;
  }

  const viewBox = first?.getAttribute?.('viewBox');
  if (viewBox) {
    const parts = viewBox
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (
      parts.length === 4 &&
      Number.isFinite(parts[2]) &&
      Number.isFinite(parts[3]) &&
      parts[2] > 0 &&
      parts[3] > 0
    ) {
      return `${parts[2]} / ${parts[3]}`;
    }
  }

  return '';
}

function percentFromFraction(rawValue) {
  const parsed = Number.parseFloat(String(rawValue || '').trim());
  if (!Number.isFinite(parsed)) return '0%';
  return `${parseFloat((parsed * 100).toFixed(4))}%`;
}

function isDefaultNumber(rawValue, defaultValue) {
  const parsed = Number.parseFloat(String(rawValue || '').trim());
  return Number.isFinite(parsed) && Math.abs(parsed - defaultValue) < 0.0001;
}

function moveBaseNumberVarToDesktop(slot, baseName, defaultValue, percentName = '') {
  const raw = slot.style.getPropertyValue(baseName);
  if (!raw || isDefaultNumber(raw, defaultValue)) return false;

  const desktopName = `${baseName}-desktop`;
  if (!slot.style.getPropertyValue(desktopName)) {
    slot.style.setProperty(desktopName, raw.trim());
  }
  slot.style.setProperty(baseName, String(defaultValue));

  if (percentName) {
    const desktopPctName = `${percentName}-desktop`;
    if (!slot.style.getPropertyValue(desktopPctName)) {
      slot.style.setProperty(desktopPctName, percentFromFraction(raw));
    }
    slot.style.setProperty(percentName, percentFromFraction(defaultValue));
  }

  return true;
}

function hasNonDefaultNumber(slot, name, defaultValue) {
  const raw = slot.style.getPropertyValue(name);
  if (!raw) return false;
  return !isDefaultNumber(raw, defaultValue);
}

function hasLogoEditorOverrides(wrapper, slot) {
  if (!slot) return false;
  const hover = wrapper?.getAttribute?.('data-logo-hover') || 'none';
  if (hover && hover !== 'none') return true;
  if (hasNonDefaultNumber(slot, '--pb-logo-tx', 0)) return true;
  if (hasNonDefaultNumber(slot, '--pb-logo-ty', 0)) return true;
  if (hasNonDefaultNumber(slot, '--pb-logo-scale', 1)) return true;
  if (slot.style.getPropertyValue('--pb-logo-slot-width')) return true;
  if (slot.style.getPropertyValue('--pb-logo-slot-height')) return true;

  for (const breakpoint of LOGO_BREAKPOINTS) {
    if (hasNonDefaultNumber(slot, `--pb-logo-tx-${breakpoint}`, 0)) return true;
    if (hasNonDefaultNumber(slot, `--pb-logo-ty-${breakpoint}`, 0)) return true;
    if (hasNonDefaultNumber(slot, `--pb-logo-scale-${breakpoint}`, 1)) return true;
    if (slot.style.getPropertyValue(`--pb-logo-slot-width-${breakpoint}`)) return true;
    if (slot.style.getPropertyValue(`--pb-logo-slot-height-${breakpoint}`)) return true;
  }

  return false;
}

function syncLogoSizing(slot) {
  if (!slot || slot.hasAttribute(LOGO_SIZED_ATTR)) return false;
  const hasHeight =
    !!slot.style.getPropertyValue('--pb-logo-slot-height') ||
    LOGO_BREAKPOINTS.some((breakpoint) =>
      slot.style.getPropertyValue(`--pb-logo-slot-height-${breakpoint}`)
    );
  if (!hasHeight) return false;
  slot.setAttribute(LOGO_SIZED_ATTR, 'true');
  return true;
}

function syncLogoEngagement(wrapper, slot) {
  if (!slot || slot.hasAttribute(LOGO_ENGAGED_ATTR)) return false;
  if (!hasLogoEditorOverrides(wrapper, slot)) return false;
  slot.setAttribute(LOGO_ENGAGED_ATTR, 'true');
  return true;
}

function moveBaseSlotWidthToDesktop(slot) {
  const raw = slot.style.getPropertyValue('--pb-logo-slot-width');
  if (!raw) return false;
  if (!slot.style.getPropertyValue('--pb-logo-slot-width-desktop')) {
    slot.style.setProperty('--pb-logo-slot-width-desktop', raw.trim());
  }
  slot.style.removeProperty('--pb-logo-slot-width');
  return true;
}

function migrateLegacyBaseLogoVars(slot) {
  let changed = false;
  if (moveBaseNumberVarToDesktop(slot, '--pb-logo-tx', 0, '--pb-logo-tx-pct')) changed = true;
  if (moveBaseNumberVarToDesktop(slot, '--pb-logo-ty', 0, '--pb-logo-ty-pct')) changed = true;
  if (moveBaseNumberVarToDesktop(slot, '--pb-logo-scale', 1)) changed = true;
  if (moveBaseSlotWidthToDesktop(slot)) changed = true;
  return changed;
}

function ensurePromotedAnchorClass(anchor, image) {
  if (!anchor || matchesAny(anchor, LOGO_ANCHOR_SELECTORS)) return false;
  if (image?.classList?.contains('cs-logo')) {
    anchor.classList.add('cs-logo');
  } else {
    anchor.classList.add('logo');
  }
  return true;
}

function ensureLogoWrapperAttrs(wrapper) {
  let changed = false;
  if (!wrapper.hasAttribute('data-logo-hover')) {
    wrapper.setAttribute('data-logo-hover', 'none');
    changed = true;
  }

  if (wrapper.querySelector('svg')) {
    if (wrapper.getAttribute('data-pb-logo-source-locked') !== '1') {
      wrapper.setAttribute('data-pb-logo-source-locked', '1');
      changed = true;
    }
  } else if (wrapper.hasAttribute('data-pb-logo-source-locked')) {
    wrapper.removeAttribute('data-pb-logo-source-locked');
    changed = true;
  }

  return changed;
}

function wrapLogoInnerNodes(wrapper) {
  if (!wrapper || wrapper.querySelector(`.${LOGO_SLOT_CLASS}`)) return false;

  const innerNodes = findLogoInnerNodes(wrapper);
  if (innerNodes.length === 0) return false;

  const doc = wrapper.ownerDocument;
  const slot = doc.createElement('span');
  slot.className = LOGO_SLOT_CLASS;
  const aspectRatio = inferLogoAspectRatio(innerNodes);
  slot.setAttribute(
    'style',
    `--pb-logo-tx:0;--pb-logo-ty:0;--pb-logo-tx-pct:0%;--pb-logo-ty-pct:0%;--pb-logo-scale:1;${aspectRatio ? `--pb-logo-aspect-ratio:${aspectRatio};` : ''}`
  );

  const firstNode = innerNodes[0];
  firstNode.parentNode.insertBefore(slot, firstNode);
  for (const node of innerNodes) {
    slot.appendChild(node);
  }
  return true;
}

function ensureSlotStyle(wrapper) {
  const slot = wrapper?.querySelector?.(`.${LOGO_SLOT_CLASS}`);
  if (!slot) return false;

  let changed = false;
  const ensureProp = (name, value) => {
    if (slot.style.getPropertyValue(name)) return;
    slot.style.setProperty(name, value);
    changed = true;
  };

  if (migrateLegacyBaseLogoVars(slot)) changed = true;

  ensureProp('--pb-logo-tx', '0');
  ensureProp('--pb-logo-ty', '0');
  ensureProp('--pb-logo-tx-pct', percentFromFraction(slot.style.getPropertyValue('--pb-logo-tx')));
  ensureProp('--pb-logo-ty-pct', percentFromFraction(slot.style.getPropertyValue('--pb-logo-ty')));
  ensureProp('--pb-logo-scale', '1');

  for (const breakpoint of LOGO_BREAKPOINTS) {
    const txName = `--pb-logo-tx-${breakpoint}`;
    const tyName = `--pb-logo-ty-${breakpoint}`;
    const txPctName = `--pb-logo-tx-pct-${breakpoint}`;
    const tyPctName = `--pb-logo-ty-pct-${breakpoint}`;
    if (slot.style.getPropertyValue(txName) && !slot.style.getPropertyValue(txPctName)) {
      slot.style.setProperty(txPctName, percentFromFraction(slot.style.getPropertyValue(txName)));
      changed = true;
    }
    if (slot.style.getPropertyValue(tyName) && !slot.style.getPropertyValue(tyPctName)) {
      slot.style.setProperty(tyPctName, percentFromFraction(slot.style.getPropertyValue(tyName)));
      changed = true;
    }
  }

  if (!slot.style.getPropertyValue('--pb-logo-aspect-ratio')) {
    const ratio = inferLogoAspectRatio(Array.from(slot.querySelectorAll(LOGO_INNER_SELECTOR)));
    if (ratio) {
      slot.style.setProperty('--pb-logo-aspect-ratio', ratio);
      changed = true;
    }
  }

  if (syncLogoEngagement(wrapper, slot)) changed = true;
  if (syncLogoSizing(slot)) changed = true;

  return changed;
}

/**
 * Normalize logo wrappers below rootElement.
 *
 * @param {Element} rootElement
 * @param {object} [options]
 * @param {Function} [options.parser] Reserved for callers that need to pass a
 * DOM parser factory; this function operates on the provided DOM root.
 * @returns {boolean} true when the DOM was changed
 */
export function normalizeLogoSlots(rootElement, options = {}) {
  if (!rootElement || typeof rootElement.querySelectorAll !== 'function') return false;
  intrinsicAspectResolver =
    typeof options.resolveIntrinsicAspect === 'function' ? options.resolveIntrinsicAspect : null;
  try {
    return normalizeLogoSlotsInner(rootElement);
  } finally {
    intrinsicAspectResolver = null;
  }
}

function normalizeLogoSlotsInner(rootElement) {
  const wrappers = new Set();
  let changed = false;
  if (matchesAny(rootElement, LOGO_ANCHOR_SELECTORS)) wrappers.add(rootElement);
  for (const wrapper of rootElement.querySelectorAll(LOGO_ANCHOR_SELECTORS.join(','))) {
    wrappers.add(wrapper);
  }
  const structuralAnchors = [];
  if (isStructuralLogoCandidate(rootElement)) structuralAnchors.push(rootElement);
  for (const anchor of rootElement.querySelectorAll('a')) {
    if (isStructuralLogoCandidate(anchor)) structuralAnchors.push(anchor);
  }
  for (const anchor of structuralAnchors) {
    const mark = anchor.querySelector(LOGO_INNER_SELECTOR);
    // Class promotion is persisted intentionally: every later logo owner uses
    // the canonical selector rather than re-running structural inference.
    if (ensurePromotedAnchorClass(anchor, mark)) changed = true;
    wrappers.add(anchor);
  }

  const bareImages = [];
  if (matchesAny(rootElement, LOGO_BARE_IMAGE_SELECTORS)) bareImages.push(rootElement);
  for (const img of rootElement.querySelectorAll(LOGO_BARE_IMAGE_SELECTORS.join(','))) {
    bareImages.push(img);
  }

  for (const img of bareImages) {
    const existingWrapper = closestLogoWrapper(img);
    if (existingWrapper) {
      wrappers.add(existingWrapper);
      continue;
    }
    const parent = img.parentElement;
    if (parent?.tagName === 'A') {
      if (ensurePromotedAnchorClass(parent, img)) changed = true;
      wrappers.add(parent);
    }
  }

  for (const wrapper of wrappers) {
    if (!hasUsableLogoInner(wrapper)) continue;
    if (wrapLogoInnerNodes(wrapper)) changed = true;
    if (ensureSlotStyle(wrapper)) changed = true;
    if (ensureLogoWrapperAttrs(wrapper)) changed = true;
  }

  return changed;
}
